import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import appStore from '../../../storage/AppStore'
import Select from '../../../ui/Select'
import Btn from "../../../ui/Btn";
import {InputNumber} from "primereact/inputnumber";
import systemStore from "../../../storage/SystemStore";

const FootballResultView = observer(({ game, role = "MGR" }) => {
  const m = game.resultModel;
  if (!m) return null;
  const canEdit = (role === "MGR" || role === "TRN") && game.result?.status === 'draft';

  const [side, setSide] = useState("side1");
  const [period, setPeriod] = useState("H1");
  const [eventType, setEventType] = useState("goal");
  const [min, setMin] = useState(10);
  const [playerId, setPlayerId] = useState(-1);
  const [playerName, setPlayerName] = useState('')
  const [note, setNote] = useState(0)

  const isValid = [side, period, eventType, min, playerId, playerName, note]
      .every(v => v != null);

  const periods = [
    {id: "1H", name: appStore.tr.first_half},
    {id: "2H", name: appStore.tr.second_half},
    {id: "ET1", name: appStore.tr.extended_time1},
    {id: "ET2", name: appStore.tr.extended_time2},
    {id: "PENS", name: appStore.tr.penalties},
  ]

  const event_types = [
    {id: "goal", name: appStore.tr.goal},
    {id: "own_goal", name: appStore.tr.own_goal},
    {id: "yellow", name: appStore.tr.yellow_card},
    {id: "red", name: appStore.tr.red_card},
  ]

  const score = m.score;

  const ensurePeriods = (periodId) => {
    if (periodId === "ET1" || periodId === "ET2") m.ensureExtraTime();
    if (periodId === "PENS") m.ensurePenalties?.();
  };


  const onAddEvent = () => {
    if (isValid) {
      ensurePeriods(period);
      const ev_id = m.addEvent({
        side: side,
        periodId: period,
        eventType: eventType,
        timeMin: min,
        playerId: playerId,
        playerName: playerName,
        note: note
      })
      setEventType(null)

      game.saveResults()
          .then(() => systemStore.setSuccess(appStore.tr.successfully_saved))
          .catch((err) => {
            systemStore.setError(err.message)
            m.removeEvent(ev_id)
            game.prepareResultForSave()
          })
    }
  }

  const onFinish = () => {
    game.finishGame()
        .then(() => {
          systemStore.setSuccess(appStore.tr.successfully_saved)
        })
        .catch(err => systemStore.setError(err.message))
  }

  return (
      <div className="game-page">
        {canEdit && (
            <div className="flex justify-content-between align-items-center mb-2">
              <div className="font-bold">{appStore.tr.game_flow}</div>
              <div className="font-bold">{score.side1}:{score.side2}</div>
            </div>
        )}

        {canEdit && (
            <div className="game-flow-form mb-3">
              <div className={"form-row"}>
                <Select options={[{id: 'side1', name: game.company.name}, {
                  id: 'side2',
                  name: game.guest_company_id ? game.guest_company.name : appStore.tr.side2
                }]}
                        onChange={(v) => {
                          setSide(v)
                          setPlayerId(null)
                          setNote(0)
                        }}
                        label={appStore.tr.side}
                        value={side}
                />
              </div>
              <div className={"form-row"}>
                <Select options={side === 'side1' ? game.team_members_1 : [{id:0, name: '???'}]}
                        onChange={(v) => {
                          setPlayerId(v)
                        }}
                        onChangeObject={(p) => {
                          setNote(p.forma_number || 0)
                          setPlayerName(`[${p.forma_number || 0}] ${p.title}`)
                        }}
                        label={appStore.tr.player}
                        value={playerId}
                        itemTemplate={(item) => {
                          return `[${item.forma_number || 0}] ${item.title}`
                        }}
                        valueTemplate={(item) => {
                          if (item) {
                            return `[${item.forma_number || 0}] ${item.title}`
                          } else {
                            return appStore.tr.please_select
                          }
                        }}
                />
                <InputNumber value={note} onValueChange={(e) => setNote(e.value)}
                             prefix={`${appStore.tr.forma_number}: `} min={1} max={100} size={9}
                             className={"p-inputtext-sm"}/>
              </div>
              <div className={"form-row"}>
                <Select options={periods} onChange={(p) => {
                  setPeriod(p)
                }} label={appStore.tr.period} value={period}/>
                <InputNumber value={min} onValueChange={(e) => setMin(e.value)}
                             prefix={`${appStore.tr.minute}: `} min={0} max={160} size={9}
                             className={"p-inputtext-sm"}/>
                <Select
                    options={event_types}
                    onChange={(ev) => {
                      setEventType(ev)
                    }}
                    label={appStore.tr.match_event}
                    value={eventType}
                />
              </div>
              <div className={"form-row justify-content-between"}>
                <Btn icon={"pi pi-plus"} label={appStore.tr.add} onClick={onAddEvent} disabled={!isValid}/>
                <Btn icon={"pi pi-check"} label={appStore.tr.game_finished} onClick={onFinish}/>
              </div>
            </div>
        )}


        <div className="font-bold mb-2">{appStore.tr.timeline}</div>
        <div className="flex flex-column gap-2">
          {m.timeline.length === 0 && <div className="text-500">{appStore.tr.no_events_yet}</div>}

          {m.timeline.map((e) => (
              <div
                  key={e.id}
                  className="flex justify-content-between align-items-center p-2"
              >
                <div className="flex flex-column">
                  <div className="font-bold">
                    {e.time_min}'
                    • {badge(e.type)} • {e.side === 'side1' ? game.company.name : (game.guest_company_id ? game.guest_company.name : appStore.tr.side2)}
                  </div>
                  <div className="text-500">
                    {appStore.tr[e.period_label]} • {e.player_name ?? "—"}
                  </div>
                </div>

                {canEdit && (
                    <Btn icon={"pi pi-trash"} onClick={() => {
                      const ev = e
                      m.removeEvent(e.id)
                      game.saveResults()
                          .then(() => systemStore.setSuccess(appStore.tr.successfully_saved))
                          .catch((err) => {
                            systemStore.setError(err.message)
                            m.addEvent({
                              side: ev.side,
                              periodId: ev.periodId,
                              eventType: ev.eventType,
                              timeMin: ev.min,
                              playerId: ev.player_id,
                              playerName: ev.player_name,
                              note: ev.note
                            })
                            game.prepareResultForSave()
                          })
                    }}/>
                )}
              </div>
          ))}
        </div>
      </div>
  );
});

export default FootballResultView;

function badge(type) {
  switch (type) {
    case "goal": return `⚽ ${appStore.tr.goal}`;
    case "own_goal": return `🥅 ${appStore.tr.own_goal}`;
    case "yellow": return `🟨 ${appStore.tr.yellow_card}`;
    case "red": return `🟥 ${appStore.tr.red_card}`;
    case "pen_scored": return "✅ Penalty scored";
    case "pen_missed": return "❌ Penalty missed";
    default: return type;
  }
}
